import { NavLink as RouterNavLink, NavLinkProps } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';

import {
  ColorsV2,
  Flex,
  IconNames,
  Text,
  useBreakpoint,
  useThemeColors,
} from '@electricjs/arc';

import Nav from '../SideNavBase';
import { SideNavItem } from '../SideNavItem';

import isVisible from '@/helpers/isVisible';
import { useUserHasElectricPermission } from '@/hooks/useUserHasElectricPermission';
import { ElectricPermission } from '@/types/users';

import electric_logo_only_black from '@/assets/electric_logo_only_black.svg';
import { ThemeContext, Themes } from '@/components/ThemeProvider';
import { useContext } from 'react';

/**
 * Creates a wrapper when an onClick method is provided.
 * This is primarily used to close the mobile menu when a menu item is clicked.
 */
function NavLinkWithOnClick(onClick?: () => void) {
  return function NavLink(props: NavLinkProps) {
    return <RouterNavLink {...(onClick ? { onClick } : {})} {...props} />;
  };
}

const InternalSideNavWrapper = ({
  mobileOnMenuClick,
}: {
  mobileOnMenuClick?: () => void;
}) => {
  const { md } = useBreakpoint();
  const { storefrontCatalogManager, itHubInternalBillingFeature } = useFlags();
  const { themeName, setThemeValue } = useContext(ThemeContext);
  const [whiteColor] = useThemeColors([ColorsV2.WHITE_STATIC]);

  const canReadInternalOrders = useUserHasElectricPermission(
    ElectricPermission.ORDERS_READ
  );

  const canReadProductProfiles = useUserHasElectricPermission(
    ElectricPermission.PRODUCTS_READ
  );

  // If the mobile menu is open, we want to close it when a menu item is clicked
  const NavLink = mobileOnMenuClick
    ? NavLinkWithOnClick(mobileOnMenuClick)
    : RouterNavLink;

  const navItems: Array<{
    text: string;
    component?: JSX.Element;
    icon?: IconNames;
    visible?: boolean;
  }> = [
    {
      text: 'Back to IT Management',
      component: NavLink && <NavLink to="/" />,
      icon: 'arrow-left',
    },
    {
      text: 'Applications',
      icon: 'app',
      component: NavLink && <NavLink to="/internal/applications" />,
    },
    {
      text: 'Billing',
      icon: 'piggy-bank',
      component: NavLink && <NavLink to="/internal/billing" />,
      visible: itHubInternalBillingFeature,
    },
    {
      text: 'Catalog',
      icon: 'shop',
      component: NavLink && <NavLink to="/internal/catalog/products" />,
      visible: storefrontCatalogManager && canReadProductProfiles,
    },
    {
      text: 'Order management',
      icon: 'truck',
      component: NavLink && <NavLink to="/internal/order-management" />,
      visible: canReadInternalOrders,
    },
    {
      text: 'Create an invoice',
      icon: 'list',
      component: NavLink && <NavLink to="/internal/invoices/new" />,
    },
    {
      text: 'Organization management',
      icon: 'bolt',
      component: NavLink && <NavLink to="/internal/organization-management" />,
    },
    {
      text: 'Warehouse',
      icon: 'warehouse',
      component: NavLink && <NavLink to="/internal/warehouse" />,
    },
  ];

  return (
    <Nav mobilePositionTop="auto">
      {md ? (
        <Flex justifyContent="center" alignItems="center">
          <div
            onClick={() => {
              setThemeValue(
                themeName === Themes.DARK ? Themes.LIGHT : Themes.DARK
              );
            }}
            // This is to allow the user to toggle the theme using the keyboard
            onKeyDown={e => {
              if (e.key === 'Enter') {
                setThemeValue(
                  themeName === Themes.DARK ? Themes.LIGHT : Themes.DARK
                );
              }
            }}
            role="button"
            tabIndex={0}
            aria-label="Toggle theme"
            style={{
              cursor: 'pointer',
              paddingTop: '8px',
            }}
            // Show browser tooltip when hovering over the logo
            title="Toggle theme">
            <img
              src={electric_logo_only_black}
              alt="Electric Internal Logo"
              style={{
                marginRight: '1.6rem',
                filter: 'invert(1)',
              }}
            />
          </div>
          <Text variant="heading-3" color={whiteColor}>
            Internal Portal
          </Text>
        </Flex>
      ) : null}
      {navItems
        ? navItems
            .filter(isVisible)
            .map(item => <SideNavItem key={item.text} {...item} />)
        : null}
    </Nav>
  );
};

export default InternalSideNavWrapper;
